import { FormEvent, useCallback, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { clearRefreshUnavailable404 } from '../api/authRefresh';
import { persistStoredRefreshToken } from '../auth/sessionToken';
import { useI18n } from '../i18n';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { persistIdentifierForLoginOption } from './lastLoginStorage';
import { persistLastSignInMethod } from './lastSignInMethod';
import { seedProfileFromLiteLogin } from './profileLoginSync';
import { serverRoleMatchesLoginType } from './authEntryPolicy';
import { roleIdFromJwt, profileRoleIdFromData } from '../auth/sessionBookingRole';
import { translateLoginOption } from '../i18n';
import {
  isValidEmailAddress,
  normalizeOtpCode,
  sendEmailOtpCode,
  verifyEmailOtpCode,
  friendlyOtpSendFailureSummary,
} from './emailOtpAuth';
import { localizeOtpFailureMessage } from '../i18n/localizeServerMessage';
import { loadPersistedOtpLoginAttempt, persistOtpLoginAttempt } from '../utils/authAttemptStorage';
import type { LoginOption } from './loginConstants';

export type EmailOtpStep = 'email' | 'code';

export function useEmailOtpLogin(loginType: LoginOption = 'private client') {
  const { t } = useI18n();
  const { applyLoginSuccess, isLoggedIn } = useAuth();
  const { setView } = useAppNavigation();
  const [step, setStep] = useState<EmailOtpStep>('email');
  const [email, setEmail] = useState(() => loadPersistedOtpLoginAttempt(loginType)?.email ?? '');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorFriendly, setErrorFriendly] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);

  const clearErrors = useCallback(() => {
    setErrorFriendly(null);
    setErrorDetails(null);
  }, []);

  const onChangeEmail = useCallback(
    (value: string) => {
      setEmail(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onChangeCode = useCallback(
    (value: string) => {
      setCode(value);
      clearErrors();
    },
    [clearErrors],
  );

  const onBackToEmail = useCallback(() => {
    setStep('email');
    setCode('');
    setCodeSent(false);
    clearErrors();
  }, [clearErrors]);

  const onSendCode = useCallback(async () => {
    if (isLoggedIn || submitting) {
      return;
    }
    const trimmedEmail = email.trim();
    if (!isValidEmailAddress(trimmedEmail)) {
      setErrorFriendly(t('auth.portal.invalidEmail'));
      setErrorDetails(null);
      return;
    }

    setSubmitting(true);
    clearErrors();
    persistOtpLoginAttempt(loginType, { email: trimmedEmail });
    try {
      const result = await sendEmailOtpCode(trimmedEmail, loginType);
      if (result.ok) {
        setEmail(trimmedEmail);
        setStep('code');
        setCodeSent(true);
        return;
      }
      setErrorFriendly(
        localizeOtpFailureMessage(
          friendlyOtpSendFailureSummary(result.friendly, {
            unauthorized: result.unauthorized,
            notAvailable: t('auth.portal.otpNotAvailable'),
            userNotRegistered: t('auth.portal.otpUserNotRegistered'),
          }),
          t,
        ),
      );
      setErrorDetails(result.details);
    } finally {
      setSubmitting(false);
    }
  }, [clearErrors, email, isLoggedIn, loginType, submitting, t]);

  const onVerifyCode = useCallback(async () => {
    if (isLoggedIn || submitting) {
      return;
    }
    const trimmedEmail = email.trim();
    const trimmedCode = normalizeOtpCode(code);
    if (!isValidEmailAddress(trimmedEmail)) {
      setErrorFriendly(t('auth.portal.invalidEmail'));
      setErrorDetails(null);
      onBackToEmail();
      return;
    }
    if (trimmedCode.length < 4) {
      setErrorFriendly(t('auth.portal.invalidCode'));
      setErrorDetails(null);
      return;
    }

    setSubmitting(true);
    clearErrors();
    try {
      const result = await verifyEmailOtpCode(trimmedEmail, trimmedCode, loginType);
      if (!result.ok) {
        setErrorFriendly(localizeOtpFailureMessage(result.friendly, t));
        setErrorDetails(result.details);
        return;
      }
      const tokenRoleId = roleIdFromJwt(result.accessToken) ?? profileRoleIdFromData(result.parsed);
      if (!serverRoleMatchesLoginType(loginType, tokenRoleId)) {
        setErrorFriendly(
          t('auth.portal.otpWrongServerRole', {
            roleId: String(tokenRoleId ?? '?'),
            type: translateLoginOption(loginType, t),
          }),
        );
        setErrorDetails(null);
        return;
      }
      persistIdentifierForLoginOption(loginType, trimmedEmail);
      persistLastSignInMethod('otp');
      applyLoginSuccess(result.parsed, result.accessToken, trimmedEmail, loginType);
      if (result.refreshToken) {
        persistStoredRefreshToken(result.refreshToken);
        clearRefreshUnavailable404();
      }
      void seedProfileFromLiteLogin({ email: trimmedEmail });
      setCode('');
      setView('home', { replace: true });
    } finally {
      setSubmitting(false);
    }
  }, [
    applyLoginSuccess,
    clearErrors,
    code,
    email,
    isLoggedIn,
    loginType,
    onBackToEmail,
    setView,
    submitting,
    t,
  ]);

  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (step === 'email') {
        void onSendCode();
        return;
      }
      void onVerifyCode();
    },
    [onSendCode, onVerifyCode, step],
  );

  return {
    step,
    email,
    code,
    submitting,
    codeSent,
    errorFriendly,
    errorDetails,
    onChangeEmail,
    onChangeCode,
    onBackToEmail,
    onSendCode,
    onVerifyCode,
    onSubmit,
    clearErrors,
  };
}
